<template>
  <button
    v-bind="rootProps"
    :class="[
      'sb-button',
      `sb-button--${variant}`,
      {
        'sb-button--clear': appearance === 'clear',
        'sb-button--disabled': isDisabled,
        'sb-button--small': size === 'small',
        'sb-button--large': size === 'large',
        'sb-button--full': isFullWidth,
        'sb-button--has-icon-left': icon,
        'sb-button--has-icon-right': iconRight,
        'sb-button--loading': isLoading,
        'sb-button--align-left': alignLeft,
      },
    ]"
    :aria-disabled="isDisabled || isLoading"
    :aria-label="descriptionTextId ? undefined : label"
    :aria-busy="isLoading"
    :aria-describedby="descriptionTextId"
    :aria-pressed="isPressed"
    :type="type"
    @click="handleClick"
  >
    <SbIcon v-if="icon" :name="icon" aria-hidden="true" />
    <span class="sb-button__label">
      <slot>{{ label }}</slot>
    </span>
    <SbIcon v-if="iconRight" :name="iconRight" aria-hidden="true" />
    <SbLoading
      v-if="isLoading"
      type="spinner"
      size="normal"
      aria-hidden="true"
    />
    <slot name="pin" />
  </button>
</template>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import type { ButtonHTMLAttributes } from 'vue'
import SbIcon from '../Icon'
import type { IconName } from '../Icon'
import SbLoading from '../Loading'
import type {
  SharedButtonType,
  SharedButtonAppearance,
  SharedButtonVariant,
  SharedButtonSize,
} from '../shared/button/types'

export type SbButtonProps = {
  label: string
  variant?: SharedButtonVariant
  size?: SharedButtonSize
  appearance?: SharedButtonAppearance
  /** [type docs](https://www.w3schools.com/tags/att_button_type.asp) */
  type?: SharedButtonType
  isDisabled?: boolean
  isLoading?: boolean
  /** button takes the full width of the container */
  isFullWidth?: boolean | null
  descriptionTextId?: string
  /** Icon name left of the label. You can use [lucide icons](https://lucide.dev/icons) or the the Storyblok icons */
  icon?: IconName
  /** Icon name right of the label. You can use [lucide icons](https://lucide.dev/icons) or custom Storyblok icons */
  iconRight?: IconName
  /** Align Button left to align with surrounding content. This is mainly for CLEAR button types, due to the lack of visible borders of button. */
  alignLeft?: boolean
  /** Button is in pressed state */
  isPressed?: boolean | undefined
  // eslint-disable-next-line vue/prop-name-casing
} & /* @vue-ignore */ ButtonHTMLAttributes

const props = withDefaults(defineProps<SbButtonProps>(), {
  variant: 'primary',
  size: 'medium',
  appearance: 'filled',
  type: 'submit',
  isDisabled: false,
  isLoading: false,
  isFullWidth: false,
  isPressed: undefined,
  descriptionTextId: undefined,
  icon: undefined,
  iconRight: undefined,
})

const rootProps = reactiveOmit(props, [
  'label',
  'variant',
  'size',
  'appearance',
  'type',
  'isDisabled',
  'isLoading',
  'isFullWidth',
  'descriptionTextId',
  'icon',
  'iconRight',
  'alignLeft',
  'isPressed',
])

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent): void => {
  if (!props.isDisabled && !props.isLoading) {
    emit('click', event)
  }
}

defineSlots<{
  /**
   * Adds a custom label. This can be a component or text.
   * Note:  It is not intended that you add a custom icon here.
   *        For Icon buttons, please use the SbIconButton.
   *        For Accessibility puposes you will still need to add a descriptive label using the "label" prop.
   */
  default(): unknown
  pin: (props: {}) => unknown
}>()
</script>
